import { t } from '@lingui/core/macro';
import { useMemo } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type SelectProps,
} from '@/components/Form/pickers/primitives/select';
import { cn } from '@/lib/utils';

type OpportunityType = 'pipeline' | 'closedWon';

type OpportunityTypeOption = {
  id: OpportunityType;
  name: string;
};

type Props = Omit<
  SelectProps<OpportunityTypeOption, false>,
  'items' | 'value' | 'onValueChange' | 'onChange' | 'itemToStringValue' | 'itemToStringLabel'
> & {
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  value: 'pipeline' | 'closedWon';
  onChange: (oppType: 'pipeline' | 'closedWon') => void;
};

const OpportunityTypeSelect = ({
  value,
  onChange,
  className,
  disabled = false,
  size = 'large',
  placeholder = t`Select Opp Type`,
  ...props
}: Props) => {
  const oppTypeOptions = useMemo<OpportunityTypeOption[]>(
    () => [
      { id: 'pipeline', name: t`Pipeline` },
      { id: 'closedWon', name: t`Closed Won` },
    ],
    [],
  );

  const selectedOption = useMemo(
    () => oppTypeOptions.find((option) => option.id === value) ?? null,
    [oppTypeOptions, value],
  );

  return (
    <Select
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name}
      disabled={disabled}
      size={size}
      value={selectedOption}
      onValueChange={(option) => onChange(option?.id ?? 'pipeline')}
      {...props}
    >
      <SelectTrigger className={cn('w-40', className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {oppTypeOptions.map((item: OpportunityTypeOption) => (
          <SelectItem key={item.id} value={item}>
            {item.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default OpportunityTypeSelect;
